import { TransportationTypeIcon } from '@/components/icons/TransportationTypeIcon';
import type {
  BookedTripDTO,
  LegDTO,
  SearchJourneyOffersDTO,
  TripDTO,
} from '@/types/dto/agent';
import { formatDay, formatTime, stripTimeZoneOffset } from '@/utils/dateTime';
import { Icons, Typography } from '@packages/webshop-design-system';
import { type FC, type ReactNode } from 'react';

export type TripInfoSource = BookedTripDTO | SearchJourneyOffersDTO;

interface TripInfoStop {
  name?: string;
}

interface TripInfoLeg {
  arrivalTime?: string;
  departureTime?: string;
  destinationStop?: TripInfoStop | null;
  originStop?: TripInfoStop | null;
  ptMode?: LegDTO['ptMode'];
}

interface TripInfoSummary {
  legs: Array<TripInfoLeg>;
}

const tripToLeg = (
  trip?: TripDTO | BookedTripDTO | SearchJourneyOffersDTO
): TripInfoLeg | undefined => {
  if (!trip) return undefined;

  return {
    arrivalTime: trip.arrivalTime,
    departureTime: trip.departureTime,
    destinationStop: trip.destinationStop,
    originStop: trip.originStop,
    ptMode: 'legs' in trip ? trip.legs?.[0]?.ptMode : undefined,
  };
};

const hasJourneyTrips = (
  trip: TripInfoSource
): trip is SearchJourneyOffersDTO => 'trips' in trip;

const getTripInfoSummary = (
  trip?: TripInfoSource | null
): TripInfoSummary | null => {
  if (!trip) return null;

  const legs: Array<TripInfoLeg> = hasJourneyTrips(trip)
    ? (trip.trips ?? []).flatMap((journeyTrip) => {
        const journeyTripLegs = journeyTrip.legs ?? [];
        return journeyTripLegs.length
          ? journeyTripLegs
          : (tripToLeg(journeyTrip) ?? []);
      })
    : (trip.legs ?? []);
  const fallbackLeg = tripToLeg(trip);
  const renderableLegs = legs.filter(
    (leg) => leg.originStop?.name || leg.destinationStop?.name
  );
  if (!renderableLegs.length && fallbackLeg) {
    renderableLegs.push(fallbackLeg);
  }

  return {
    legs: renderableLegs.filter(
      (leg) => leg.originStop?.name || leg.destinationStop?.name
    ),
  };
};

interface TripInfoEndpointProps {
  icon: ReactNode;
  stopName?: string;
  time?: string;
}

const TripInfoEndpoint: FC<TripInfoEndpointProps> = ({
  icon,
  stopName,
  time,
}) => {
  return (
    <div className="grid w-full grid-cols-[20px,1fr,40px,40px]">
      {icon}
      <Typography variant="body2-bold">{stopName}</Typography>
      <Typography variant="body2" className="text-neutral">
        {time && formatDay(stripTimeZoneOffset(time))}
      </Typography>
      <Typography variant="body2" className="text-right">
        {formatTime(time)}
      </Typography>
    </div>
  );
};

const TripInfoLegBlock: FC<{ leg: TripInfoLeg }> = ({ leg }) => (
  <div>
    <TripInfoEndpoint
      icon={
        <TransportationTypeIcon ptMode={leg.ptMode} className="text-primary" />
      }
      stopName={leg.originStop?.name}
      time={leg.departureTime}
    />

    <div className="ml-[7px] h-3 w-0.5 bg-neutral-light" />

    <TripInfoEndpoint
      icon={
        <Icons.destination
          height={12}
          width={12}
          className="ml-0.5 text-primary"
        />
      }
      stopName={leg.destinationStop?.name}
      time={leg.arrivalTime}
    />
  </div>
);

const TripInfo: FC<{
  trip?: TripInfoSource | null;
  testId?: string;
}> = ({ trip, testId }) => {
  const summary = getTripInfoSummary(trip);

  if (!summary?.legs.length) return null;

  return (
    <div className="flex w-full flex-col gap-3 px-1 py-4" data-testid={testId}>
      {summary.legs.map((leg, index) => (
        <TripInfoLegBlock key={index} leg={leg} />
      ))}
    </div>
  );
};

export default TripInfo;
